import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { IMAGES, RETIREES_BREAKFAST, RETIREES_LUNCHEON, PHOENIX_IN_MOTION } from "@/lib/constants";

// NOTE (Feb 2026): IMAGES.detroit (hero + conference featured card background)
// and IMAGES.hero (newsletter band background) are flagged for later Tier 2
// photo replacement. Do not swap until confirmed photography is approved.
import { toast } from "sonner";
import {
  ArrowUpRight,
  Calendar,
  MapPin,
  Newspaper,
  Megaphone,
  Building2,
  GraduationCap,
  HandHeart,
  Sparkles,
  Award,
  Flame,
  Inbox,
  Mail,
  Send,
  Clock,
  Utensils,
  DollarSign,
  Phone,
  Camera,
} from "lucide-react";

// Tag → label + accent color
const TAGS = [
  { id: "all", label: "All" },
  { id: "outreach", label: "Community Outreach" },
  { id: "project-100", label: "Project 100" },
  { id: "engine-11", label: "Historic Engine 11" },
  { id: "retiree", label: "Retiree Fellowship" },
  { id: "phoenix", label: "Phoenix News" },
];

const TAG_META = {
  "outreach": { label: "Community Outreach", icon: HandHeart },
  "project-100": { label: "Project 100", icon: GraduationCap },
  "engine-11": { label: "Historic Engine 11", icon: Building2 },
  "retiree": { label: "Retiree Fellowship", icon: Award },
  "phoenix": { label: "Phoenix News", icon: Flame },
  // Legacy 60th/conference tag IDs still in seeded events fall back to a neutral Phoenix News label.
  "60th": { label: "Phoenix News", icon: Flame },
  "conference": { label: "Phoenix News", icon: Flame },
};

const CROSS_LINKS = [
  {
    icon: HandHeart,
    t: "Community Outreach",
    b: "Programs across the year that ground Phoenix in the everyday life of Detroit.",
    cta: "Explore Outreach",
    to: "/programs-outreach",
  },
];

function fmtDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

// Event IDs whose dates are NOT yet confirmed for public publication.
// These events are still happening — only the printed/calendar date is
// withheld until Phoenix announces it via text blast (Phoenix Text Connect).
// Cards still render fully; the date row is suppressed.
const DATE_TBA_IDS = new Set([
  "evt-project100",       // Project 100 Recruitment Information Session
  "evt-backpack-2026",    // Annual Backpack Giveaway
  "evt-senior-support",   // Senior Support & Smoke Detector Drive
]);

function TagBadge({ tag }) {
  const meta = TAG_META[tag];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-accent text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded">
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function EventCard({ e }) {
  return (
    <div className="card-dark p-7 flex flex-col" data-testid={`event-${e.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {e.is_featured && (
          <span className="inline-block text-[10px] tracking-[0.28em] uppercase font-accent text-black bg-amber-500 px-2 py-1 rounded">
            Featured
          </span>
        )}
        <TagBadge tag={e.tag} />
      </div>
      <div className="font-display text-2xl text-white mt-4 leading-tight">
        {e.title}
      </div>
      {!DATE_TBA_IDS.has(e.id) && (
        <div className="mt-4 flex items-center gap-2 text-sm text-amber-500">
          <Calendar className="h-4 w-4" />
          {fmtDate(e.date)}
        </div>
      )}
      <div className="mt-1 flex items-center gap-2 text-sm text-slate-400">
        <MapPin className="h-4 w-4" />
        {e.location}
      </div>
      <p className="text-slate-400 mt-4 text-sm leading-relaxed flex-1">
        {e.description}
      </p>
    </div>
  );
}

function NewsCard({ n }) {
  return (
    <div className="card-dark p-8 flex flex-col" data-testid={`news-${n.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="overline">{fmtDate(n.date)}</span>
        <TagBadge tag={n.tag} />
      </div>
      <div className="font-display text-2xl text-white mt-3">{n.title}</div>
      <p className="text-slate-400 mt-3 leading-relaxed flex-1">{n.description}</p>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div
      className="card-dark p-10 text-center"
      data-testid="empty-state"
    >
      <Inbox className="h-7 w-7 text-amber-500 mx-auto" />
      <div className="font-display text-xl text-white mt-4">
        No {label} to share just yet.
      </div>
      <p className="text-slate-400 mt-3 text-sm leading-relaxed max-w-md mx-auto">
        Check back as plans firm up. Phoenix shares {label.toLowerCase()} here as
        they are confirmed and aligned with final organizational planning.
      </p>
    </div>
  );
}

export default function EventsNews() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState("all");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Fail-soft: if the backend is sleeping/unreachable, log it but never
    // surface an uncaught promise rejection (which would otherwise pop the
    // React dev error overlay and persist across navigation — see May 23
    // 502 report on /membership). Page will just render with empty lists.
    Promise.all([
      api.get("/events").then((r) => setEvents(r.data || [])).catch((e) => {
        console.warn("[events] fetch failed (fail-soft):", e?.message || e);
      }),
      api.get("/news").then((r) => setNews(r.data || [])).catch((e) => {
        console.warn("[news] fetch failed (fail-soft):", e?.message || e);
      }),
    ]).finally(() => setLoading(false));
  }, []);

  const featuredEvents = useMemo(
    () => events.filter((e) => e.is_featured),
    [events]
  );

  const filteredEvents = useMemo(() => {
    const list = events.filter((e) => !e.is_featured);
    if (activeTag === "all") return list;
    return list.filter((e) => e.tag === activeTag);
  }, [events, activeTag]);

  const filteredNews = useMemo(() => {
    if (activeTag === "all") return news;
    return news.filter((n) => n.tag === activeTag);
  }, [news, activeTag]);

  async function handleNewsletter(e) {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email: newsletterEmail });
      toast.success("You're on the list. Phoenix will be in touch.");
      setNewsletterEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="events-news-page">
      <PageMeta
        title="Events & News"
        description="Phoenix events and news — community outreach, Project 100, retiree fellowship at Historic Engine 11, and ongoing Engine 11 preservation."
        path="/events-news"
      />
      <PageHero
        overline="Events & News"
        title="What's happening at the Phoenix."
        subtitle="Community outreach days, Project 100 sessions, active and retired firefighter fellowship, monthly gatherings at Historic Engine 11, and ongoing Phoenix programs — stay close to the work."
        image={IMAGES.eventsHero}
        imagePosition="center top"
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <a
            href="#upcoming-events"
            className="btn-gold w-full sm:w-auto justify-center"
            data-testid="hero-upcoming"
          >
            Upcoming Events <ArrowUpRight className="h-4 w-4" />
          </a>
          <a
            href="#subscribe"
            className="btn-primary w-full sm:w-auto justify-center"
            data-testid="hero-subscribe"
          >
            Subscribe to Updates
          </a>
        </div>
      </PageHero>

      {/* ----- MISSION BAND ----- */}
      <section
        className="px-5 lg:px-8 -mt-6 lg:-mt-10"
        data-testid="events-mission-band"
      >
        <div className="max-w-7xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500 text-black">
            <Newspaper className="h-6 w-6" />
          </span>
          <p className="text-slate-100 text-base lg:text-lg leading-relaxed max-w-4xl">
            News, gatherings, milestones, and announcements{" "}
            <span className="text-amber-400 italic">
              the Phoenix family wants you to know about
            </span>{" "}
            — shared as they are confirmed and aligned with final Phoenix
            organizational planning.
          </p>
        </div>
      </section>

      {/* (Featured 2027 bridge removed — evergreen Events & News page) */}

      {/* ----- FILTER BAR ----- */}
      <section className="pb-2" data-testid="events-filter-bar">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {TAGS.map((t) => {
              const active = activeTag === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTag(t.id)}
                  data-testid={`filter-${t.id}`}
                  className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-accent rounded-full border transition-colors ${
                    active
                      ? "bg-amber-500 text-black border-amber-500"
                      : "bg-transparent text-slate-300 border-white/10 hover:border-amber-500/50 hover:text-amber-400"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ----- UPCOMING EVENTS ----- */}
      <section className="py-12 lg:py-16" data-testid="upcoming-events">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading overline="Upcoming" title="Events." />
          {loading ? (
            <div className="text-slate-400 mt-8" data-testid="events-loading">
              Loading events…
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="mt-10">
              <EmptyState label="events" />
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((e) => (
                <EventCard key={e.id} e={e} />
              ))}
            </div>
          )}
          <p className="text-slate-500 text-xs leading-relaxed mt-10 max-w-3xl">
            Event details — including dates, locations, and program elements —
            are subject to change as final planning is confirmed.
          </p>
        </div>
      </section>

      {/* ----- NEWS ----- */}
      <section className="py-12 lg:py-28 bg-[#0E0E0E]" data-testid="phoenix-news">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading overline="From the Firehouse" title="News." />
          {loading ? (
            <div className="text-slate-400 mt-8" data-testid="news-loading">
              Loading news…
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="mt-10">
              <EmptyState label="news" />
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredNews.map((n) => (
                <NewsCard key={n.id} n={n} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ----- MONTHLY RETIREES BREAKFAST (RECURRING · 1ST WED · APR–SEP) ----- */}
      <section className="pt-12 lg:pt-24 pb-6 lg:pb-8" data-testid="retirees-breakfast-highlight">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Flyer image — rendered at natural aspect so it never crops on any viewport */}
            <div className="lg:col-span-5 flex flex-col">
              <div className="rounded-3xl overflow-hidden ring-ember bg-black p-2 sm:p-3">
                <img
                  src={RETIREES_BREAKFAST.flyerImage}
                  alt="Phoenix Monthly Retirees Breakfast — Eastside / Westside Retirees · Bridging the Gap · 1st Wednesday of every month · doors open at 9:00am · catered by Last Minute Gourmet at Historic Engine 11"
                  className="w-full h-auto rounded-2xl block"
                  data-testid="retirees-breakfast-flyer"
                />
              </div>
              <p
                className="text-slate-500 text-xs leading-relaxed mt-3 italic max-w-md"
                data-testid="retirees-breakfast-flyer-caption"
              >
                Recurring monthly gathering. See the Next Breakfast date at
                right for the upcoming first-Wednesday gathering.
              </p>
            </div>

            {/* Details */}
            <div className="lg:col-span-7">
              <div className="card-dark p-8 lg:p-10 h-full flex flex-col">
                <div className="overline flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-500" /> Retiree Fellowship · Recurring
                </div>
                <h3 className="font-display text-3xl lg:text-4xl text-white mt-3 leading-tight">
                  Monthly Retirees Breakfast
                </h3>
                <div className="overline text-amber-400 mt-2">
                  Eastside / Westside Retirees · Bridging the Gap
                </div>
                <div className="divider-gold" />

                <p className="text-slate-300 leading-relaxed">
                  A monthly fellowship gathering for Phoenix retirees and the
                  wider Detroit Fire Department retiree community — hosted at
                  Historic Engine 11. {RETIREES_BREAKFAST.schedule}
                </p>

                <dl
                  className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"
                  data-testid="retirees-breakfast-details"
                >
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 sm:col-span-2">
                    <dt className="overline flex items-center gap-2 text-amber-400">
                      <Calendar className="h-3.5 w-3.5" /> Next Breakfast
                    </dt>
                    <dd
                      className="text-white mt-1 font-display text-xl"
                      data-testid="retirees-breakfast-next-date"
                    >
                      {RETIREES_BREAKFAST.nextDate}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-slate-800 p-4">
                    <dt className="overline flex items-center gap-2 text-slate-400">
                      <Clock className="h-3.5 w-3.5" /> Doors Open
                    </dt>
                    <dd className="text-slate-200 mt-1">
                      {RETIREES_BREAKFAST.doorsOpen}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-slate-800 p-4">
                    <dt className="overline flex items-center gap-2 text-slate-400">
                      <DollarSign className="h-3.5 w-3.5" /> Cost
                    </dt>
                    <dd className="text-slate-200 mt-1">
                      {RETIREES_BREAKFAST.cost}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-slate-800 p-4 sm:col-span-2">
                    <dt className="overline flex items-center gap-2 text-slate-400">
                      <MapPin className="h-3.5 w-3.5" /> Location
                    </dt>
                    <dd className="text-slate-200 mt-1">
                      {RETIREES_BREAKFAST.location}
                    </dd>
                  </div>
                  <div className="rounded-xl border border-slate-800 p-4 sm:col-span-2">
                    <dt className="overline flex items-center gap-2 text-slate-400">
                      <Utensils className="h-3.5 w-3.5" /> Breakfast
                    </dt>
                    <dd className="text-slate-200 mt-1">
                      Catered by {RETIREES_BREAKFAST.caterer}
                    </dd>
                  </div>
                </dl>

                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#subscribe"
                    className="btn-gold w-full sm:w-auto justify-center"
                    data-testid="retirees-breakfast-subscribe"
                  >
                    Subscribe for Updates <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link
                    to="/contact?type=general&subject=Monthly%20Retirees%20Breakfast"
                    className="btn-ghost w-full sm:w-auto justify-center"
                    data-testid="retirees-breakfast-contact"
                  >
                    Contact Phoenix
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- ANNUAL PHOENIX RETIREES LUNCHEON (companion card to the Breakfast above) ----- */}
      <section className="pt-6 lg:pt-8 pb-12 lg:pb-24" data-testid="retirees-luncheon-highlight">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="card-dark p-6 lg:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              {/* Compact flyer thumbnail */}
              <div className="lg:col-span-4">
                <div className="rounded-2xl overflow-hidden ring-1 ring-amber-500/20 bg-black p-1.5 sm:p-2">
                  <img
                    src={RETIREES_LUNCHEON.flyerImage}
                    alt="Annual Phoenix Retirees Luncheon — Sound the Alarm. Don't miss this run. Good food, music, BYOB, see old friends at Historic Engine 11."
                    className="w-full h-auto rounded-xl block"
                    data-testid="retirees-luncheon-flyer"
                  />
                </div>
              </div>

              {/* Compact details */}
              <div className="lg:col-span-8">
                <div className="overline flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-500" /> Retiree Fellowship · Annual
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-white mt-3 leading-tight">
                  {RETIREES_LUNCHEON.title}
                </h3>
                <div className="overline text-amber-400 mt-2 italic normal-case tracking-normal text-sm">
                  {RETIREES_LUNCHEON.tagline}
                </div>

                <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mt-5 flex items-start gap-3">
                  <Calendar className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
                  <div className="text-sm text-slate-100 leading-relaxed">
                    <span className="overline text-amber-400 block mb-1">Next Luncheon Date</span>
                    {RETIREES_LUNCHEON.nextDate
                      ? RETIREES_LUNCHEON.nextDate
                      : "Announced separately via Phoenix member text blast."}
                  </div>
                </div>

                <ul
                  className="mt-5 flex flex-wrap gap-2"
                  data-testid="retirees-luncheon-highlights"
                >
                  {RETIREES_LUNCHEON.highlights.map((h) => (
                    <li
                      key={h}
                      className="text-xs tracking-[0.18em] uppercase font-accent text-amber-400/90 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-full"
                    >
                      {h}
                    </li>
                  ))}
                </ul>

                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border border-slate-800 p-3 flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                    <div>
                      <div className="overline text-slate-400">Location</div>
                      <div className="text-slate-200 mt-0.5">{RETIREES_LUNCHEON.location}</div>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-800 p-3 flex items-start gap-2">
                    <Phone className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
                    <div>
                      <div className="overline text-slate-400">{RETIREES_LUNCHEON.rsvpInstruction}</div>
                      <div className="text-slate-200 mt-0.5">{RETIREES_LUNCHEON.rsvpPhone}</div>
                    </div>
                  </div>
                </dl>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#subscribe"
                    className="btn-gold w-full sm:w-auto justify-center"
                    data-testid="retirees-luncheon-subscribe"
                  >
                    Subscribe for Updates <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link
                    to="/contact?type=general&subject=Annual%20Retirees%20Luncheon"
                    className="btn-ghost w-full sm:w-auto justify-center"
                    data-testid="retirees-luncheon-contact"
                  >
                    Contact Phoenix
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- HISTORIC ENGINE 11 ACTIVITY ----- */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="engine11-activity">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <img
              src={IMAGES.engine11}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-red-950/60" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-amber-500" /> Historic Engine 11
                </div>
                <h3 className="font-display text-3xl lg:text-5xl text-white mt-3 leading-[1.05]">
                  Engine 11 milestones, preservation updates,
                  <span className="block text-amber-500">and stories — published as they happen.</span>
                </h3>
                <p className="text-slate-300 mt-5 leading-relaxed max-w-2xl">
                  Engine 11 milestones, building activity, and preservation
                  stories are shared here as they unfold — honoring Detroit&apos;s
                  firefighting legacy and the gathering place of the Phoenix.
                </p>
              </div>
              <div className="lg:col-span-4 flex">
                <Link
                  to="/historic-engine-11"
                  className="btn-gold w-full justify-center"
                  data-testid="engine11-activity-cta"
                >
                  Explore Engine 11 <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- SUBMIT NEWS / EVENT ----- */}
      <section className="py-12 lg:py-24" data-testid="submit-content">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Share With The Phoenix"
            title="Have news or an event to share?"
            subtitle="If you have a Phoenix-related story, milestone, or community event you'd like considered for Phoenix communications, please reach out. Submissions are reviewed in accordance with Phoenix editorial discretion."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="card-dark p-8 flex flex-col"
              data-testid="submit-story-card"
            >
              <Send className="h-7 w-7 text-amber-500" />
              <div className="font-display text-2xl text-white mt-5">
                Submit News / Story
              </div>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                Share a Phoenix-related news item, milestone, or story idea for
                consideration in Phoenix communications.
              </p>
              <Link
                to="/contact?type=media&subject=News%20%2F%20Story%20Submission"
                className="btn-gold mt-6 w-full justify-center"
                data-testid="submit-story-cta"
              >
                Submit News / Story <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div
              className="card-dark p-8 flex flex-col"
              data-testid="submit-event-card"
            >
              <Calendar className="h-7 w-7 text-amber-500" />
              <div className="font-display text-2xl text-white mt-5">
                Submit an Event
              </div>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                Share a community event, gathering, or partnership opportunity
                for Phoenix consideration.
              </p>
              <Link
                to="/contact?type=general&subject=Event%20Submission"
                className="btn-primary mt-6 w-full justify-center"
                data-testid="submit-event-cta"
              >
                Submit an Event <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ----- PHOENIX IN MOTION — 10-photo restrained gallery (added May 23, 2026) ----- */}
      <section className="py-12 lg:py-24" data-testid="phoenix-in-motion">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs tracking-[0.32em] uppercase font-accent">
              <Camera className="h-3.5 w-3.5" /> Phoenix in Motion
            </div>
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white mt-4 leading-tight">
              The life of Phoenix.
            </h3>
            <div className="divider-gold" />
            <p className="text-slate-400 mt-6 text-sm sm:text-base leading-relaxed">
              Founders, conventions, the first Project 100 cohort, community
              days, brotherhood, sisterhood, and the fellowship that keeps the
              Phoenix moving forward.
            </p>
          </div>

          <div className="mt-10 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {PHOENIX_IN_MOTION.map((p, i) => (
              <figure
                key={p.src}
                className="group relative overflow-hidden rounded-2xl border border-slate-800 ring-1 ring-amber-500/10 bg-black/40"
                data-testid={`pim-item-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading={i < 4 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]"
                  />
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent px-4 py-3">
                  <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-amber-400">{p.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ----- INLINE NEWSLETTER ----- */}
      <section
        id="subscribe"
        className="py-12 lg:py-24 bg-[#0E0E0E]"
        data-testid="inline-newsletter"
      >
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-8 lg:p-12">
            <img
              src={IMAGES.hero}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/65 via-black/45 to-red-950/40" />
            <div className="relative">
              <div className="overline flex items-center gap-2">
                <Mail className="h-4 w-4 text-amber-500" /> Stay Close to the Work
              </div>
              <h3 className="font-display text-3xl lg:text-5xl text-white mt-3 leading-[1.05]">
                Subscribe to Phoenix
                <span className="block text-amber-500">updates.</span>
              </h3>
              <p className="text-slate-300 mt-5 leading-relaxed max-w-2xl">
                Get Phoenix news, event announcements, outreach moments,
                Engine 11 milestones, and community updates delivered to your
                inbox.
              </p>
              <form
                onSubmit={handleNewsletter}
                className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
                data-testid="newsletter-form"
              >
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(ev) => setNewsletterEmail(ev.target.value)}
                  placeholder="Your email address"
                  data-testid="newsletter-email"
                  className="flex-1 bg-black/50 border border-white/10 focus:border-amber-500/60 rounded-full px-5 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold justify-center"
                  data-testid="newsletter-submit"
                >
                  {submitting ? "Subscribing…" : "Subscribe"}
                </button>
              </form>
              <p
                className="text-slate-500 text-xs leading-relaxed mt-4 max-w-2xl"
                data-testid="newsletter-privacy-copy"
              >
                Phoenix respects your inbox. Unsubscribe any time. By
                subscribing, you acknowledge that Phoenix may use the email
                provided to send Phoenix updates in accordance with our{" "}
                <Link
                  to="/privacy"
                  className="text-amber-400 hover:text-amber-300 underline-offset-4 hover:underline"
                  data-testid="newsletter-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- CROSS-LINKS ----- */}
      <section className="py-12 lg:py-28" data-testid="events-cross-links">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Explore More of the Phoenix"
            title="The bigger picture."
            subtitle="Events and news are part of a larger Phoenix story — six decades of brotherhood, sisterhood, advocacy, and service to Detroit."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CROSS_LINKS.map((c) => (
              <div
                key={c.t}
                className="card-dark p-7 flex flex-col"
                data-testid={`events-cross-${c.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <c.icon className="h-7 w-7 text-amber-500" />
                <div className="font-display text-xl text-white mt-5">{c.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                  {c.b}
                </div>
                <Link to={c.to} className="btn-ghost mt-6 w-full justify-center">
                  {c.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- CLOSING CTA ----- */}
      <section className="py-16 lg:py-32" data-testid="events-closing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="overline">Phoenix · Detroit · Stay Close</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-[1.05]">
            Stay close to
            <span className="block text-amber-500">the work.</span>
          </h2>
          <div className="divider-gold mx-auto" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href="#subscribe"
              className="btn-gold w-full justify-center"
              data-testid="closing-subscribe"
            >
              Subscribe
            </a>
            <Link
              to="/contact?type=media&subject=News%20%2F%20Story%20Submission"
              className="btn-primary w-full justify-center"
              data-testid="closing-submit-story"
            >
              Submit a Story
            </Link>
            <Link
              to="/contact"
              className="btn-ghost w-full justify-center"
              data-testid="closing-contact"
            >
              Contact Phoenix
            </Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-xs text-slate-500">
            <Flame className="h-3.5 w-3.5 text-amber-500" />
            <span className="tracking-[0.28em] uppercase font-accent">
              Phoenix · Detroit · Service · Legacy
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
